import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const ArticleSlideshow = ({ images, autoScroll = true, interval = 4000 }) => {
  const scrollRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  
  useEffect(() => {
    if (!autoScroll || isPaused || !scrollRef.current) return;
    
    const container = scrollRef.current;
    let scrollAmount = 0;
    const scrollStep = 1;
    const maxScroll = container.scrollWidth - container.clientWidth;
    
    const scroll = () => {
      if (!container) return;
      
      scrollAmount += scrollStep;
      
      if (scrollAmount >= maxScroll) {
        // Reset to beginning smoothly
        scrollAmount = 0;
        container.scrollTo({ left: 0, behavior: 'auto' });
      } else {
        container.scrollTo({ left: scrollAmount, behavior: 'auto' });
      }
    };
    
    const intervalId = setInterval(scroll, 30); // Smooth continuous scroll
    
    return () => clearInterval(intervalId);
  }, [autoScroll, isPaused]);
  
  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };
  
  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };
  
  if (!images || images.length === 0) return null;
  
  return (
    <div 
      className="article-slideshow relative my-6"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="article-slideshow"
    >
      {/* Navigation Arrows */}
      <button 
        onClick={scrollLeft}
        className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all"
        aria-label="Scroll left"
        data-testid="slideshow-prev-btn"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button 
        onClick={scrollRight}
        className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all"
        aria-label="Scroll right"
        data-testid="slideshow-next-btn"
      >
        <ChevronRight className="h-5 w-5" />
      </button>
      
      {/* Scrolling Container */}
      <div 
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide py-2 px-8"
        style={{ 
          scrollbarWidth: 'none', 
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {images.map((img, index) => (
          <div 
            key={index}
            className="flex-shrink-0 rounded-xl overflow-hidden shadow-lg"
            style={{ maxWidth: '320px', maxHeight: '400px' }}
          >
            <img 
              src={img.src} 
              alt={img.alt || `Slide ${index + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            {img.caption && (
              <div className="bg-slate-900 text-white text-sm px-3 py-2 text-center">
                {img.caption}
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* Indicator dots */}
      <div className="flex justify-center gap-2 mt-3">
        {images.length > 1 && images.map((_, index) => (
          <div 
            key={index}
            className="w-2 h-2 rounded-full bg-slate-300"
          />
        ))}
      </div>
    </div>
  );
};

export default ArticleSlideshow;
